import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { Cacheable } from 'ts-cacheable';
import { MergeEntityPayload, SharedResource } from 'app/modules/shared-resources/shared-resources.types';
import { FolderResource, FileResource } from 'app/shared/modules/folders/folders.types';
import { AccountService } from 'app/core';
import { Store } from '@ngxs/store';
import { AccountActions } from '@state/account/actions';
import { Account } from '@core/models';

const sharedResourceCacheBuster$ = new Subject<void>();

@Injectable({
  providedIn: 'root',
})
export class SharedResourcesService {
  // Private
  private _sharedResource: BehaviorSubject<SharedResource | null>;
  private _folder: BehaviorSubject<FolderResource | null>;
  private _file: BehaviorSubject<FileResource | null>;

  private get accountId(): string {
    return this.accountService.getCurrentAccount()?.id;
  }

  /**
   * Constructor
   *
   * @param {HttpClient} _httpClient
   * @param accountService
   * @param store
   */
  constructor(
    private _httpClient: HttpClient,
    private readonly accountService: AccountService,
    private store: Store,
  ) {
    this._sharedResource = new BehaviorSubject(null);
    this._folder = new BehaviorSubject(null);
    this._file = new BehaviorSubject(null);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for contact
   */
  get sharedResource$(): Observable<SharedResource> {
    return this._sharedResource.asObservable();
  }

  /**
   * Getter for folder
   */
  get folder$(): Observable<FolderResource> {
    return this._folder.asObservable();
  }

  /**
   * Getter for contact
   */
  get file$(): Observable<FileResource> {
    return this._file.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get contacts
   *
   * @param sortField
   * @param sortDirection
   */
  @Cacheable({
    cacheBusterObserver: sharedResourceCacheBuster$,
  })
  getSharedResource(token: string): Observable<SharedResource> {
    return this._httpClient.get<any>(`sharing/shared_resources/${token}`).pipe(
      map((response) => {
        const account: Account = {
          id: response.shared_resource.account.id.toString(),
          name: response.shared_resource.account.name as string,
        };
        this.accountService.setAccount(account);
        this.store.dispatch(new AccountActions.SetAccount(account));
        this._sharedResource.next(response?.shared_resource);
        return response?.shared_resource;
      }),
    );
  }

  /**
   * Get contacts
   *
   * @param sortField
   * @param sortDirection
   */
  getFolder(folderId?: number): Observable<FolderResource> {
    if (folderId) {
      return this.sharedResource$.pipe(
        take(1),
        switchMap((sharedResource) =>
          this._httpClient
            .get<any>(`sharing/shared_resources/${sharedResource.token}/folder_resources/${folderId}`)
            .pipe(
              map((response) => {
                this._folder.next(response.folder_resource);
                return response.folder_resource;
              }),
            ),
        ),
      );
    } else {
      return this.sharedResource$.pipe(
        take(1),
        switchMap((sharedResource) =>
          this._httpClient.get<any>(`sharing/shared_resources/${sharedResource.token}/folder_resources`).pipe(
            map((response) => {
              this._folder.next(response.folder_resource);
              return response.folder_resource;
            }),
          ),
        ),
      );
    }
  }

  getFile(folderId: number, fileResourceId: number): Observable<FileResource> {
    return this.sharedResource$.pipe(
      take(1),
      switchMap((sharedResource) =>
        this._httpClient
          .get<any>(
            `sharing/shared_resources/${sharedResource.token}/folder_resources/${folderId}/file_resources/${fileResourceId}`,
          )
          .pipe(
            map((response) => {
              this._file.next(response.file_resource);
              return response.file_resource;
            }),
          ),
      ),
    );
  }

  getRootFile(): Observable<FileResource> {
    return this.sharedResource$.pipe(
      take(1),
      switchMap((sharedResource) =>
        this._httpClient.get<any>(`sharing/shared_resources/${sharedResource.token}/file_resources`).pipe(
          map((response) => {
            this._file.next(response.file_resource);
            return response.file_resource;
          }),
        ),
      ),
    );
  }

  merge(payload: MergeEntityPayload): Observable<any> {
    return this._httpClient.post(`merge`, payload).pipe(catchError((err) => of(err)));
  }
}
